import { lazyWithRetry as lazy } from './lazyWithRetry';
import { withProtected } from './withProtected';
import { withFeatureFlag } from './withFeatureFlag';
import { withOrganisationAdminFeature } from './withOrganisationAdminFeature';
import { CONSTITUENTS_FEATURE, PARTNER_PROGRAM_FLAG } from '../constants/partnerGrants';
import { ORG_DOCUMENT_HISTORY_FLAG } from '../constants/orgDocumentHistory';
import { CUSTOM_EMAILS_FLAG } from '../constants/emailTemplates';

export const organisation = [
	{
		path: '/organisations/:organisationId/',
		Component: withProtected(
			lazy(() => import('../pages/Organisation')),
			[]
		),
		children: [
			{
				path: '/organisations/:organisationId/',
				Component: lazy(() => import('../pages/Organisation/Dashboard')),
				handle: {
					key: 'pages.organisation.dashboard.title'
				}
			},
			{
				path: '/organisations/:organisationId/dashboard/:role',
				Component: lazy(() => import('../pages/Organisation/Dashboards/Generic')),
				handle: {
					key: 'pages.organisation.dashboard.title'
				}
			},
			{
				// Dashboards hub (pill tabs per role-scoped dashboard); /dashboard/:role kept for deep links.
				path: '/organisations/:organisationId/dashboards',
				Component: lazy(() => import('../pages/Organisation/Dashboards/DashboardsHub')),
				handle: {
					key: 'pages.organisation.dashboard.title'
				}
			},
			{
				path: '/organisations/:organisationId/staff',
				Component: lazy(() => import('../pages/Organisation/Staff')),
				handle: {
					key: 'pages.organisation.staff.title'
				}
			},
			{
				path: '/organisations/:organisationId/developer',
				Component: lazy(() => import('../pages/Organisation/Developer')),
				handle: {
					key: 'pages.organisation.developer.title'
				}
			},
			{
				// Guarded like its nav item: organisation admins only, and only with the
				// `constituents` grant. The roster is the whole student body, so a member
				// typing the URL must 404 (PTEC-1117).
				path: '/organisations/:organisationId/constituents',
				Component: withOrganisationAdminFeature(
					lazy(() => import('../pages/Organisation/Constituents/Constituents')),
					CONSTITUENTS_FEATURE
				),
				handle: {
					key: 'pages.organisation.constituents.title'
				}
			},
			{
				// Flag-gated, not just hidden in the nav: the page is a wireframe on mocked
				// enrolment data and the flag is locked off in demo and production for
				// v3.15.0, so the URL must 404 there too.
				path: '/organisations/:organisationId/partner-program',
				Component: withFeatureFlag(
					lazy(() => import('../pages/Organisation/Partner')),
					PARTNER_PROGRAM_FLAG
				),
				handle: {
					key: 'pages.organisation.partner.title'
				}
			},
			{
				// Additive mockup of the consolidated settings hub (RFC). Does not replace
				// the dashboard route; reachable for review via a temporary nav item.
				path: '/organisations/:organisationId/settings',
				Component: lazy(() => import('../pages/Organisation/OrganisationSettings')),
				handle: {
					key: 'pages.organisation.settings.title'
				}
			},
			{
				path: '/organisations/:organisationId/branding',
				Component: lazy(() => import('../pages/Organisation/Branding')),
				handle: {
					key: 'pages.organisation.branding.title'
				}
			},
			{
				// Flag-gated like its nav item. The page itself guards on the organisation
				// admin role; the grant only decides whether it is editable.
				path: '/organisations/:organisationId/email-settings',
				Component: withFeatureFlag(
					lazy(() => import('../pages/Organisation/EmailSettings')),
					CUSTOM_EMAILS_FLAG
				),
				handle: {
					key: 'pages.organisation.emailSettings.title'
				}
			},
			{
				path: '/organisations/:organisationId/employees',
				Component: lazy(() => import('../pages/Organisation/Employees')),
				handle: {
					key: 'pages.organisation.staff.title'
				}
			},
			{
				path: '/organisations/:organisationId/students',
				Component: lazy(() => import('../pages/Organisation/Students')),
				handle: {
					key: 'pages.organisation.staff.title'
				}
			},
			{
				path: '/organisations/:organisationId/agent-students',
				Component: lazy(() => import('../pages/Organisation/AgentStudents')),
				handle: {
					key: 'pages.organisation.staff.title'
				}
			},
			{
				path: '/organisations/:organisationId/users/:userId',
				Component: lazy(() => import('../pages/Organisation/User')),
				handle: {
					key: 'pages.organisation.staff.title'
				},
				children: [
					{
						index: true,
						Component: lazy(() => import('../pages/Organisation/UserProfile'))
					},
					{
						path: 'wallet',
						Component: lazy(() => import('../pages/Wallet/ProxyWallet')),
						children: [
							{
								index: true,
								Component: lazy(() => import('../pages/Wallet/Categories'))
							},
							{
								path: 'categories/:categoryId',
								Component: lazy(() => import('../pages/Wallet/Category'))
							},
							{
								path: 'documents/upload/:categoryId',
								Component: lazy(() => import('../pages/Wallet/Documents/Editor'))
							},
							{
								path: 'documents/:documentId',
								Component: lazy(() => import('../pages/Wallet/Documents/View'))
							},
							{
								path: 'documents/:documentId/edit',
								Component: lazy(() => import('../pages/Wallet/Documents/Editor'))
							},
							{
								path: 'documents/:documentId/preview',
								Component: lazy(() => import('../pages/Wallet/Documents/Preview'))
							},
							{
								path: 'verifications',
								Component: lazy(() => import('../pages/Verifications/List'))
							},
							{
								path: 'verifications/request',
								Component: lazy(() => import('../pages/Verifications/Request'))
							}
						]
					}
				]
			},
			{
				path: '/organisations/:organisationId/verifications',
				Component: lazy(() => import('../pages/Organisation/Verifications')),
				handle: {
					key: 'pages.organisation.verifications.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/verifications/',
						Component: lazy(() => import('../pages/Organisation/Verifications/List'))
					},
					{
						path: '/organisations/:organisationId/verifications/:verificationId',
						Component: lazy(() => import('../pages/Organisation/Verifications/Details'))
					}
				]
			},
			{
				path: '/organisations/:organisationId/shared-documents-requests',
				Component: lazy(() => import('../pages/Organisation/SharedDocumentsRequests/')),
				handle: {
					key: 'pages.organisation.sharedDocumentsRequests.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/shared-documents-requests/',
						Component: lazy(() => import('../pages/Organisation/SharedDocumentsRequests/List'))
					},
					{
						path: '/organisations/:organisationId/shared-documents-requests/editor',
						Component: lazy(() => import('../pages/Organisation/SharedDocumentsRequests/Editor'))
					},
					{
						path: '/organisations/:organisationId/shared-documents-requests/:requestId',
						Component: lazy(() => import('../pages/Organisation/SharedDocumentsRequests/Details'))
					}
				]
			},
			{
				path: '/organisations/:organisationId/shared-documents-templates',
				Component: lazy(() => import('../pages/Organisation/SharedDocumentsTemplates/')),
				handle: {
					key: 'pages.organisation.sharedDocumentsTemplates.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/shared-documents-templates/',
						Component: lazy(() => import('../pages/Organisation/SharedDocumentsTemplates/List'))
					},
					{
						path: '/organisations/:organisationId/shared-documents-templates/editor',
						Component: lazy(() => import('../pages/Organisation/SharedDocumentsTemplates/Editor'))
					},
					{
						path: '/organisations/:organisationId/shared-documents-templates/:templateId',
						Component: lazy(() => import('../pages/Organisation/SharedDocumentsTemplates/Editor'))
					}
				]
			},
			{
				path: '/organisations/:organisationId/candidate-enquiries',
				Component: lazy(() => import('../pages/Organisation/CandidateEnquiries/')),
				handle: {
					key: 'pages.organisation.enquiries.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/candidate-enquiries/',
						Component: lazy(() => import('../pages/Organisation/CandidateEnquiries/List'))
					},
					{
						path: '/organisations/:organisationId/candidate-enquiries/:requestId',
						Component: lazy(() => import('../pages/Organisation/CandidateEnquiries/Details'))
					}
				]
			},
			{
				path: '/organisations/:organisationId/bulk-upload-metrics',
				Component: lazy(() => import('../pages/Organisation/BulkUpload/')),
				handle: {
					key: 'pages.organisation.enquiries.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/bulk-upload-metrics',
						Component: lazy(() => import('../pages/Organisation/BulkUpload/List'))
					},
					{
						path: '/organisations/:organisationId/bulk-upload-metrics/:bulkUploadMetricId',
						Component: lazy(() => import('../pages/Organisation/BulkUpload/DocumentList'))
					}
				]
			},
			{
				// Org document management dashboard (PTEC-425). A sibling of the batch list
				// above rather than a child of it: this view is deliberately not scoped to a
				// single upload, which is also what lets it show Partner-API submissions.
				path: '/organisations/:organisationId/document-history',
				// Flag-gated at the route, not just hidden in the nav: hiding a menu item
				// leaves the URL open, and this page lists every document the organisation
				// has ever sent.
				Component: withFeatureFlag(
					lazy(() => import('../pages/Organisation/BulkUpload/DocumentHistory')),
					ORG_DOCUMENT_HISTORY_FLAG
				),
				handle: {
					key: 'pages.organisation.documentHistory.title'
				}
			},
			{
				path: '/organisations/:organisationId/portal',
				Component: lazy(() => import('../pages/Organisation/Portal/')),
				handle: {
					key: 'pages.organisation.portal.title'
				},
				children: [
					{
						// Index → Portal hub (pill tabs); the profile/manage/search pages below
						// remain for deep links and are embedded as tabs in the hub.
						path: '/organisations/:organisationId/portal',
						Component: lazy(() => import('../pages/Organisation/Portal/PortalHub'))
					},
					{
						path: '/organisations/:organisationId/portal/profile',
						Component: lazy(() => import('../pages/Organisation/Portal/Profile'))
					},
					{
						path: '/organisations/:organisationId/portal/search',
						Component: lazy(() => import('../pages/Organisation/Portal/Search/Search'))
					},
					{
						path: '/organisations/:organisationId/portal/search/:profileId',
						Component: lazy(() => import('../pages/Organisation/Portal/Search/ProfileDetail'))
					},
					{
						path: '/organisations/:organisationId/portal/candidateinquiries',
						Component: lazy(() => import('../pages/Organisation/Portal/CandidateInquiries'))
					},
					{
						path: '/organisations/:organisationId/portal/create',
						Component: lazy(() => import('../pages/Organisation/Portal/PortalCreateAdvert'))
					},
					{
						path: '/organisations/:organisationId/portal/manage',
						Component: lazy(() => import('../pages/Organisation/Portal/PortalView'))
					},
					{
						path: '/organisations/:organisationId/portal/adverts/:advertId',
						Component: lazy(() => import('../pages/Organisation/Portal/PortalAdvertView'))
					},
					{
						path: '/organisations/:organisationId/portal/adverts/:advertId/edit',
						Component: lazy(() => import('../pages/Organisation/Portal/PortalEditAdvert'))
					}
				]
			},
			{
				path: '/organisations/:organisationId/prospective',
				Component: lazy(() => import('../pages/Organisation/PearsanaAdmin/OrganisationProspective')),
				handle: {
					key: 'pages.admin.prospective.employee.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/prospective',
						Component: lazy(
							() =>
								import('../pages/Organisation/PearsanaAdmin/OrganisationProspective/ProspectiveOrgList')
						)
					},
					{
						path: '/organisations/:organisationId/prospective/:checkOrgId/list',
						Component: lazy(
							() => import('../pages/Organisation/PearsanaAdmin/OrganisationProspective/ProspectiveList')
						)
					}
				]
			},
			{
				path: '/organisations/:organisationId/logging',
				Component: lazy(() => import('../pages/Organisation/PearsanaAdmin/Portal')),
				handle: {
					key: 'pages.admin.logging.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/logging',
						Component: lazy(() => import('../pages/Organisation/PearsanaAdmin/Portal/Logs'))
					},
					{
						path: '/organisations/:organisationId/logging/:checkOrgId/list',
						Component: lazy(() => import('../pages/Organisation/PearsanaAdmin/Portal/List'))
					}
				]
			},
			{
				path: '/organisations/:organisationId/billing-administrator',
				Component: lazy(() => import('../pages/Organisation/BillingAdministrator')),
				handle: {
					key: 'pages.organisation.billingAdmin.title'
				},
				children: [
					{
						path: '/organisations/:organisationId/billing-administrator/account-status',
						Component: lazy(() => import('../pages/Organisation/BillingAdministrator/AccountStatus'))
					},
					{
						path: '/organisations/:organisationId/billing-administrator/account-status/order/:orderId',
						Component: lazy(() => import('../pages/Organisation/BillingAdministrator/OrderDetails'))
					},
					{
						path: '/organisations/:organisationId/billing-administrator/product-catalog',
						Component: lazy(() => import('../pages/Organisation/BillingAdministrator/ProductCatalog'))
					},
					{
						path: '/organisations/:organisationId/billing-administrator/product-catalog/checkout',
						Component: lazy(() => import('../pages/Organisation/BillingAdministrator/Checkout'))
					}
				]
			}
		]
	}
];
